// src/pages/ELearning/VerifyCertificatePage.tsx — ПУБЛИЧНАЯ
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { verifyCertificate } from '../../api/client';
import {
  CheckCircle, XCircle, Loader2, Award, User as UserIcon,
  Building2, Calendar, Trophy, ShieldCheck, Search
} from 'lucide-react';

const VerifyCertificatePage: React.FC = () => {
  const { number } = useParams<{ number: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchNum, setSearchNum] = useState(number || '');

  useEffect(() => {
    if (!number) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await verifyCertificate(number);
        setData(res);
      } catch (e) {
        setData({ valid: false, message: 'Ошибка проверки' });
      } finally {
        setLoading(false);
      }
    })();
  }, [number]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchNum.trim()) {
      navigate(`/certificate/verify/${searchNum.trim()}`);
    }
  };

  const cert = data?.certificate;
  const valid = data?.valid;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pop { 0% { transform: scale(0); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .vc-hero { animation: fadeUp 0.5s ease-out; }
        .vc-card { animation: fadeUp 0.5s ease-out 0.1s backwards; }
        .vc-icon { animation: pop 0.5s ease-out; }
        .vc-trophy { animation: float 3s ease-in-out infinite; }
        .vc-btn { transition: all 0.3s ease; }
        .vc-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.35) !important; }
      `}</style>

      <div style={styles.bg} />
      <div style={styles.container}>
        {/* ═══ Hero ═══ */}
        <div className="vc-hero" style={styles.hero}>
          <div style={styles.heroIcon}>
            <ShieldCheck size={32} color="#fff" />
          </div>
          <div>
            <h1 style={styles.heroTitle}>Проверка сертификата</h1>
            <p style={styles.heroSubtitle}>
              Введите номер сертификата для проверки его подлинности
            </p>
          </div>
        </div>

        {/* ═══ Форма поиска ═══ */}
        <form onSubmit={handleSearch} style={styles.searchForm}>
          <Search size={18} color="#8b8fa8" />
          <input
            type="text"
            placeholder="EL-2026-0001"
            value={searchNum}
            onChange={(e) => setSearchNum(e.target.value)}
            style={styles.searchInput}
          />
          <button type="submit" className="vc-btn" style={styles.searchBtn}>
            Проверить
          </button>
        </form>

        {/* ═══ Loading ═══ */}
        {loading && (
          <div style={styles.centered}>
            <Loader2 size={40} className="vc-icon" color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {/* ═══ Неверный ═══ */}
        {!loading && data && !valid && (
          <div className="vc-card" style={styles.errorCard}>
            <div className="vc-icon" style={{ ...styles.statusIcon, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
              <XCircle size={40} color="#fff" />
            </div>
            <h2 style={styles.errorTitle}>Сертификат не подтверждён</h2>
            <p style={styles.errorText}>{data.message || 'Проверьте номер и попробуйте снова'}</p>
            {number && (
              <div style={styles.searchedNumber}>
                Искомый номер: <strong>{number}</strong>
              </div>
            )}
          </div>
        )}

        {/* ═══ Валидный ═══ */}
        {!loading && valid && cert && (
          <div className="vc-card" style={styles.successCard}>
            <div className="vc-trophy" style={{ ...styles.statusIcon, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
              <Trophy size={40} color="#fff" />
            </div>

            <div style={styles.validBadge}>
              <CheckCircle size={16} /> Сертификат действителен
            </div>

            <h2 style={styles.certNumber}>{cert.number}</h2>

            <div style={styles.certGrid}>
              <CertRow icon={<UserIcon size={16} />} label="Владелец" value={cert.userName} />
              <CertRow icon={<Award size={16} />} label="Тест" value={cert.testTitle} />
              {cert.testCategory && (
                <CertRow icon={<ShieldCheck size={16} />} label="Категория" value={cert.testCategory} />
              )}
              <CertRow icon={<Trophy size={16} />} label="Результат" value={`${cert.score}%`} highlight />
              {cert.companyName && (
                <CertRow icon={<Building2 size={16} />} label="Компания" value={cert.companyName} />
              )}
              {cert.completedAt && (
                <CertRow icon={<Calendar size={16} />} label="Дата прохождения" value={new Date(cert.completedAt).toLocaleDateString('ru-RU')} />
              )}
            </div>

            <div style={styles.footerBrand}>
              ✨ HR LAMA — Верифицировано
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const CertRow: React.FC<{ icon: React.ReactNode; label: string; value: any; highlight?: boolean }> = ({ icon, label, value, highlight }) => (
  <div style={styles.certRow}>
    <span style={styles.certLabel}>
      {icon}
      {label}
    </span>
    <span style={{ ...styles.certValue, ...(highlight ? styles.certValueHighlight : {}) }}>{value}</span>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  bg: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 50%, #f5f0fb 100%)',
    zIndex: -1,
  },
  container: { maxWidth: 640, margin: '0 auto', padding: '48px 20px', position: 'relative' },

  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 },
  heroIcon: {
    width: 64, height: 64, borderRadius: 20,
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)',
    flexShrink: 0,
  },
  heroTitle: {
    fontSize: 26, fontWeight: 800, margin: 0,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #11998e 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  },
  heroSubtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },

  searchForm: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    borderRadius: 16, padding: '6px 6px 6px 16px',
    marginBottom: 24,
    boxShadow: '0 8px 24px rgba(31, 38, 135, 0.06)',
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none',
    fontSize: 15, padding: '10px 8px',
    background: 'transparent', fontFamily: 'monospace',
    fontWeight: 600, color: '#1a1a2e',
  },
  searchBtn: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: '#fff', border: 'none', borderRadius: 12,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.3)',
  },

  centered: { display: 'flex', justifyContent: 'center', padding: 60 },

  errorCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    borderRadius: 28, padding: 40, textAlign: 'center',
    boxShadow: '0 12px 48px rgba(31, 38, 135, 0.1)',
  },
  successCard: {
    background: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    borderRadius: 28, padding: 36,
    textAlign: 'center',
    boxShadow: '0 12px 48px rgba(31, 38, 135, 0.1)',
  },

  statusIcon: {
    width: 96, height: 96, borderRadius: 32,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  errorTitle: { fontSize: 22, fontWeight: 800, color: '#ef4444', marginBottom: 10 },
  errorText: { color: '#5a5f7a', fontSize: 14, lineHeight: 1.5, marginBottom: 20 },
  searchedNumber: {
    padding: '10px 16px',
    background: 'rgba(239, 68, 68, 0.08)',
    borderRadius: 12, fontSize: 13, color: '#5a5f7a',
    fontFamily: 'monospace',
  },

  validBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', borderRadius: 12, marginBottom: 20,
    background: 'rgba(16, 185, 129, 0.15)', color: '#10b981',
    fontSize: 13, fontWeight: 800,
  },
  certNumber: {
    fontSize: 24, fontWeight: 800,
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    fontFamily: 'monospace', letterSpacing: 1, margin: '0 0 28px',
  },

  certGrid: {
    background: 'rgba(255, 255, 255, 0.6)',
    borderRadius: 18, padding: 20,
    display: 'flex', flexDirection: 'column', gap: 14,
    marginBottom: 20,
  },
  certRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', gap: 16, textAlign: 'left',
    paddingBottom: 12, borderBottom: '1px solid rgba(0,0,0,0.04)',
  },
  certLabel: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, color: '#8b8fa8', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.5,
    flexShrink: 0,
  },
  certValue: {
    fontSize: 14, fontWeight: 600, color: '#1a1a2e',
    textAlign: 'right', wordBreak: 'break-word',
  },
  certValueHighlight: {
    fontSize: 18, fontWeight: 800,
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },

  footerBrand: {
    fontSize: 12, color: '#8b8fa8', fontWeight: 600,
    marginTop: 20, opacity: 0.7,
  },
};

export default VerifyCertificatePage;
